<template>
  <div class="trial-message">
    <message
      :grid="true"
      :modifiers="['background-blue']">
      <div class="trial-message__inner">
        <div
          class="trial-message__description"
          v-html="description" />
        <div class="trial-message__cta-wrapper">
          <a
            class="trial-message__cta button button--wide"
            :class="finalCtaJsClass"
            @click="handleClick"
            :href="ctaHref"
            :data-login-path="loginPath"
            :data-signup-path="signupPath">
            {{ finalCtaCopy }}
          </a>
        </div>
      </div>
    </message>
  </div>
</template>

<script>
import events from "@/events/index";
import Message from "@/shared/message";
import { mapGetters } from "vuex";

export default {
  name: "TrialMessage",
  components: {
    Message
  },
  props: {
    signupRoute: {
      type: String,
      required: false,
      default: ""
    },
    ctaCopy: {
      type: String,
      default: null
    },
    ctaJsClass: {
      type: String,
      default: null
    },
    piwikCategory: {
      type: String,
      default: null
    },
    plan: {
      type: Object,
      default: null,
      validator: function (plan) {
        return typeof plan.trialDays === "number";
      }
    },
    redirectToCheckoutPage: {
      type: Boolean,
      default: false
    },
    loggedInUserRedirect: {
      type: String,
      default: null
    },
    i18nKey: {
      type: String,
      default: "smallInvestment"
    }
  },
  data() {
    return { trialStartDate: new Date() };
  },
  computed: {
    ...mapGetters("layout", ["startTrialButtonCtaHref"]),
    localeI18nKey() {
      return  this.i18nKey;
    },
    trialEndDate() {
      const timestamp = this.trialStartDate;
      timestamp.setDate(timestamp.getDate() + this.plan.trialDays);

      return timestamp;
    },
    dayOfMonth() {
      return this.$d(this.trialEndDate, "dayOfMonth");
    },
    finalCtaCopy() {
      return this.ctaCopy || this.$t(`${this.localeI18nKey}.cta`, { count: this.plan.trialDays });
    },
    trialLength() {
      if (this.plan.trialDays % 31 === 0) {
        return this.$tc(`${this.localeI18nKey}.monthsCount`, this.plan.trialDays / 31);
      }
      return  this.$tc(`${this.localeI18nKey}.daysCount`, this.plan.trialDays);
    },
    description() {
      return this.$t(`${this.localeI18nKey}.description`, {
        trialLength: this.trialLength,
        dayOfMonth: this.dayOfMonth,
        price: this.plan.price.perMonth.markup
      });
    },
    finalCtaJsClass() {
      if (this.signupRoute || this.startTrialButtonCtaHref) {
        return "";
      }

      return this.ctaJsClass || "js-open-signup-popup";
    },
    loginPath() {
      if (this.redirectToCheckoutPage) {
        return this.plan.loginPath;
      }

      return null;
    },
    signupPath() {
      if (this.redirectToCheckoutPage) {
        return this.plan.signupPath;
      }

      return null;
    },
    ctaHref() {
      if (this.startTrialButtonCtaHref) {
        return this.startTrialButtonCtaHref;
      }

      if (this.signupRoute) {
        return this.signupRoute;
      }
      return this.loggedInUserRedirect || this.plan.link;
    }
  },
  methods: {
    handleClick() {
      events.track(this.piwikCategory, "1-click");
    }
  },
  i18n: {
    dateTimeFormats: {
      en: {
        dayOfMonth: {
          month: "long",
          day: "numeric"
        }
      },
      de: {
        dayOfMonth: {
          month: "long",
          day: "numeric"
        }
      }
    },
    messages: {
      en: {
        smallInvestment: {
          daysCount: " | one day | {count} days",
          monthsCount: " | one month | {count} months",
          description: "Enjoy unlimited access for {trialLength}. Love it and keep investing in yourself for only <b> {price} </b> a month, or <b>simply cancel before {dayOfMonth} and you won’t ever be charged.</b>",
          cta: "Start your free trial"
        }
      },
      de: {
        smallInvestment: {
          daysCount: " | ein Tag | {count} Tage",
          monthsCount: " | ein Monat | {count} Monate",
          description: "Testen kostet nichts: Probiere Blinkist für {trialLength} aus. <b>Kündigst du vor dem {dayOfMonth}, entstehen dir keine Kosten.</b> Danach verlängert sich das Abo um ein Jahr für <b>{price}</b> Euro pro Monat.",
          cta: "Jetzt kostenlos testen"
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.trial-message {

  &__inner {
    @include grid-css-element(12);

    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: $spacing-8 $spacing-16;

    @include media(min-width, m) {
      flex-direction: row;
      align-items: center;
      padding-right: $spacing-24;
      padding-left: $spacing-24;
    }

    @include media(min-width, l) {
      @include grid-css-element(10, 2);

      padding-right: 0;
      padding-left: 0;
    }
  }

  &__description {
    @include font-style(p);

    @include media(min-width, m) {
      margin-right: $spacing-24;
      margin-bottom: 0;
    }
  }

  &__cta-wrapper {
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
  }

  &__cta {
    @include media(max-width, m) {
      margin-top: $spacing-16;
    }
  }
}
</style>
